import * as React from 'react';
import styled from '@emotion/styled';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import { Typography as MuiTypography } from '@mui/material';
import CardContent from '@mui/material/CardContent';
import { useTranslation } from 'next-i18next';
import { CustomButton } from '../CustomButton';
import { getText } from '../../utils/get-text';
import { useAppContext } from '../../contexts/AppContext';

export interface CardContentProps {
  title?: string | undefined;
  description?: string | undefined;
  limit?: number | undefined;
  hideDescription?: boolean | undefined;
  className?: string;
}

const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

const CardContentStyled = styled(CardContent)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  textAlign: 'left',
  padding: theme.spacing(2),
  height: '100%',
  border: theme.theme_component_card_content_border || 'none',
  borderTop: 'none',
  pointerEvents: 'none',
  flex: 1,
  minHeight: 105,
  [theme.breakpoints.between('clg', 'cxl')]: {
    minHeight: 73,
  },
})) as typeof CardContent;

const CardContentInfo = styled('div')(({ theme }) => ({
  display: 'grid',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  width: '100%',
  display: 'inline-block',
  color: '#000',
  marginRight: theme.spacing(1.5),
  fontSize: '22px',
  [theme.breakpoints.between('clg', 'cxl')]: {
    fontSize: '15px',
  },
}));

const Description = styled(Typography)(({ theme }) => ({
  height: 'auto',
  fontSize: '22px',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.between('clg', 'cxl')]: {
    fontSize: '15px',
    WebkitLineClamp: 1,
  },
}));

const ButtonStyled = styled(CustomButton)(({ theme }) => ({
  display: 'flex',
  margin: 0,
  pointerEvents: 'auto',
  alignSelf: 'center',
  padding: theme.spacing(1.5, 6.75),
  marginLeft: 8,
  background: theme.theme_component_feature_grid_a_card_button_background || theme.palette.primary.main,
  color: theme.theme_component_feature_grid_a_card_button_color || theme.palette.primary.contrastText,
  borderRadius: theme.theme_component_feature_grid_b_card_button_border_radius || theme.spacing(9),
  '&:hover': {
    background: theme.theme_component_feature_grid_a_card_button_background || theme.palette.primary.main,
    color: theme.theme_component_feature_grid_b_card_button_active_background_color || theme.palette.primary.main,
  },

  [theme.breakpoints.up('cxl')]: {
    minWidth: 155,
    minHeight: 53
  },
  [theme.breakpoints.between('clg', 'cxl')]: {
    width: 108,
    height: 37,
    fontSize: 14
  },
}));

export const CollectionContentF = (props: any) => {
  const {
    description,
    title,
    limit,
    className,
    company,
    hideDeveloperName = false,
    hidePlayButton,
    hideInfoSection,
    hideDescription = true,
    hideTitle
  } = props;
  const { t } = useTranslation('card_content_c');
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const { useConfig } = useAppContext();
  const { componentOptions } = useConfig();
  

  return (
    <>
      {(title || description || company) && !hideInfoSection && (
        <CardContentStyled className="card-content">
          <CardContentInfo className="card-content-info">
            {!hideTitle && (
              <CardTitle variant={'caption'} noWrap>
                {getText(title || '', limit || 35)}
              </CardTitle>
            )}
            {!hideDeveloperName && (
              <Description variant="body2" color="textPrimary">
                {company && getText(company, limit || 35)}
              </Description>
            )}
            {!hideDescription && (
              <Description variant="body2" color="textPrimary">
                {description && getText(description, limit || 80)}
              </Description>
            )}
          </CardContentInfo>
          {!hidePlayButton && (
            <ButtonStyled
              ref={buttonRef}
              variant="contained"
              disableElevation
              disableRipple
              disableFocusRipple
              size="small"
              className="card-content-button"
            >
              {componentOptions.component_card_title_text || t('play_now', 'Play')}
            </ButtonStyled>
          )}
        </CardContentStyled>
      )}
    </>
  );
};
